/**
 * Stream API client — HLS streaming and stream token management (web)
 */
import { apiClient } from '../../../services/apiClient';
import { getApiUrl } from '../../../lib/runtimeConfig';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  switch (method) {
    case 'GET': return apiClient.get<T>(path);
    case 'POST': return apiClient.post<T>(path, body);
    case 'DELETE': return apiClient.delete<T>(path);
    default: throw new Error(`Unsupported method: ${method}`);
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StreamInfoDto {
  streamUrl: string;
  expiresAt: string;
}

export interface StreamLinkDto {
  id: string;
  locationId: string;
  locationName: string;
  streamUrl: string;
  expiresAt: string;
  instructions: StreamLinkInstructionsDto;
}

export interface StreamLinkInstructionsDto {
  vlc: string;
  sonos: string;
  generic: string;
}

export interface StreamLinkSummaryDto {
  id: string;
  locationId: string;
  locationName: string;
  streamUrl: string;
  createdByEmail: string;
  createdAt: string;
  lastUsedAt?: string | null;
  expiresAt: string;
}

export interface PlayableLocationDto {
  id: string;
  name: string;
  address?: string | null;
  hasActiveScenario: boolean;
  currentlyPlaying: boolean;
}

// ---------------------------------------------------------------------------
// Player / Locations
// ---------------------------------------------------------------------------

export const getPlayableLocations = () =>
  request<PlayableLocationDto[]>('GET', '/api/v1/player/locations');

export const getStreamInfo = async (locationId: string): Promise<StreamInfoDto> => {
  const info = await request<StreamInfoDto>('GET', `/api/v1/player/locations/${locationId}/stream`);

  // Backend returns a relative stream URL — resolve it to absolute for HLS player
  if (info.streamUrl && !info.streamUrl.startsWith('http')) {
    return { ...info, streamUrl: `${getApiUrl()}${info.streamUrl}` };
  }

  return info;
};

// ---------------------------------------------------------------------------
// Stream Tokens (External Player Links)
// ---------------------------------------------------------------------------

export const generateStreamLink = (locationId: string, expiresInDays = 30) =>
  request<StreamLinkDto>('POST', `/api/v1/player/locations/${locationId}/stream-link`, { expiresInDays });

export const listStreamLinks = () =>
  request<StreamLinkSummaryDto[]>('GET', '/api/v1/player/stream-links');

export const revokeStreamLink = (tokenId: string) =>
  request<void>('DELETE', `/api/v1/player/stream-links/${tokenId}`);
